<template>
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-5 py-5">
    <form-section :title="tr('Organization details')">
      <div class="flex flex-col gap-1.5">
        <ui-label for="organization-name">{{ tr('Organization name') }}</ui-label>
        <ui-input id="organization-name" v-model="model.name" />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Time zone') }}</ui-label>
        <ui-combobox-select
          v-model="model.timezone"
          :options="timezones"
          option-value="name"
          option-label="label"
          :search-placeholder="tr('Search') + '...'"
          :empty-text="tr('No results')"
        />
      </div>

      <div class="flex flex-col gap-2">
        <ui-label>{{ tr('Required two-factor authentication') }}</ui-label>
        <label :class="[ 'flex items-center gap-2.5 text-sm text-foreground', $root.loggedInUser.google_2fa_enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-60' ]">
          <ui-checkbox v-model="model.required_2fa" :disabled="!$root.loggedInUser.google_2fa_enabled" />
          {{ tr('Require 2FA from users in the account') }}
        </label>
      </div>

      <ui-alert v-if="!model.required_2fa && !$root.loggedInUser.google_2fa_enabled">
        <info-icon />
        <ui-alert-description>
          {{ tr('To enable this function, you must first enable two-factor authentication in your own user account on the Account page.') }}
        </ui-alert-description>
      </ui-alert>
    </form-section>

    <form-section v-if="$root.loggedInUser.company.is_demo" :title="tr('Reset account')">
      <ui-button variant="destructive" class="self-start" @click="() => openResetConfirmDialog()">
        <rotate-ccw-icon />
        {{ tr('Reset') }}
      </ui-button>
    </form-section>

    <form-section
      v-else-if="$root.loggedInUser.level === 'owner'"
      :title="tr('Delete account')"
      :description="deleteDescription"
    >
      <ui-button variant="destructive" class="self-start" @click="() => openDeleteConfirmDialog()">
        <trash-2-icon />
        {{ tr('Delete') }}
      </ui-button>
    </form-section>
  </div>
</template>

<script>
import { Info, RotateCcw, Trash2 } from '@lucide/vue';

import Company from '@/entities/Company';
import CompanyService from '@/services/Company.js';
import FormSection from '@/ui/components/FormSection.vue';

export default {
  name: 'SettingsOrganizationView',
  components: {
    FormSection,
    InfoIcon: Info,
    RotateCcwIcon: RotateCcw,
    Trash2Icon: Trash2,
  },
  data:() => ({
    model: null,
    isLoading: false,
  }),
  computed: {
    loggedInUserCompany() {
      return this.$root.currentCompany;
    },
    timezones() {
      return this.$store.getters['app/timezones'];
    },
    deleteDescription() {
      return this.tr('By pressing the button, the entire account and all data related to it will be deleted from the :app, including automations, users, connected profiles and assets.');
    },
  },
  created() {
    this.model = new Company(this.loggedInUserCompany);
  },
  methods: {
    save() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      CompanyService.update(this.$root.currentCompany.id, this.model)
        .then(resp => {
          this.$store.commit('auth/updateCompany', resp);
          this.$toast.success(this.tr('Successful save'));

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
    openResetConfirmDialog() {
      this.$confirmation.open({
        title: this.tr('Reset account'),
        content: this.tr('Are you sure you want to reset the account?') + '<br>' + this.tr('This will reset all data and settings. This action cannot be undone.'),
        labelCancel: this.tr('Cancel'),
        labelConfirm: this.tr('Confirm'),
        destructive: true,
        confirm: () => this.reset(),
      });
    },
    reset() {
      return CompanyService.reset(this.$root.currentCompany.id)
        .then(resp => {
          this.$store.dispatch('rules/resetView');
          this.$store.dispatch('posts/resetView');
          window.location = window.location.href;

          return resp;
        });
    },
    openDeleteConfirmDialog() {
      this.$confirmation.open({
        title: this.tr('Delete account'),
        content: this.tr('Are you sure you want to delete the account?') + '<br>' + this.tr('The deletion is final and cannot be revoked.'),
        labelCancel: this.tr('Cancel'),
        labelConfirm: this.tr('Confirm'),
        destructive: true,
        confirm: () => this.delete(),
      });
    },
    delete() {
      return CompanyService.delete(this.$root.currentCompany.id)
        .then(async resp => {
          await this.$store.dispatch('auth/teardown');
          this.$toast.success(this.tr('The account was successfully deleted.'));

          return resp;
        });
    },
  },
};
</script>
